<template>
	<div id="map">
		<div class="menu-container">
			<div id="menu-tooltip" class="tooltip" data-tooltip="Toggle to display a layer">
				<v-icon color="primary" v-text="'mdi-information'"></v-icon>
			</div>
			<div class="menu">
				<nav id="menu"></nav>
			</div>
			<nav id="usagebands-list"></nav>
		</div>
	</div>
</template>
<script>
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'

import axios from 'axios'
import CONFIG from '@/config'

const enc_coverage_map = new Map([
	[1, ['Overview', '#ff3300']],
	[2, ['General', '#00b200']],
	[3, ['Coastal', '#2a14ae']],
	[4, ['Approach', '#993300']],
	[5, ['Harbour', '#ff6600']],
	[6, ['Berthing', '#cc33cc']]
])

function loadUsageBands(map) {
	//ENC Coverage with Usage Bands
	let name = ''
	let color = ''
	for (var i = 1; i < enc_coverage_map.size + 1; i++) {
		name = enc_coverage_map.get(i)[0]
		color = enc_coverage_map.get(i)[1]

		map.addSource(name.toLowerCase(), {
			type: 'vector',
			tiles: [`https://primar.ecc.no/primar/vectortile_ip/anonymous/coverage/{z}/{x}/{y}.mvt`]
		})

		map.addLayer({
			id: name,
			type: 'fill',
			source: name.toLowerCase(),
			'source-layer': 'coverage',
			paint: {
				'fill-opacity': 0.5,
				'fill-color': color,
				'fill-outline-color': color
			},
			layout: {
				visibility: 'none'
			},
			filter: ['==', 'intu', i.toString()]
		})
	}
}

function loadS100Variants(map, s100variants) {
	//All S100-Variants. Should be automatic if more variants are added to the API, RTZ is included in this with nr 14
	let shortName = ''
	let name = ''
	let nr = 0
	for (var i = 0; i < s100variants.length; i++) {
		name = s100variants[i].spec.name
		shortName = s100variants[i].spec.shortName
		nr = s100variants[i].spec.nr
		if (nr !== 14) {
			//Cannot filter RTZ in this as it has its own url 
			map.addSource(shortName, {
				type: 'vector',
				tiles: ['https://primar.ecc.no/primar/vectortile_ip/anonymous/S100Coverage/{z}/{x}/{y}.mvt']
			})

			map.addLayer({
				id: name + ' Coverage',
				type: 'fill',
				source: shortName,
				'source-layer': 'S100Coverage',
				paint: {
					'fill-opacity': 0.3,
					'fill-color': '#FF9500',
					'fill-outline-color': '#00387A'
				},
				layout: { visibility: 'none' },
				filter: ['==', 'prodSpec', nr]
			})
		} else {
			map.addSource(shortName, {
				type: 'vector',
				tiles: ['https://primar.ecc.no/primar/vectortile_ip/anonymous/RTZcoverage/{z}/{x}/{y}.mvt']
			})

			map.addLayer({
				id: name + ' Coverage',
				type: 'line',
				source: shortName,
				'source-layer': 'RTZcoverage',
				paint: {
					'line-opacity': 0.3,
					'line-color': '#FF9500'
					// 'line-outline-color': '#00387A'
				},
				layout: { visibility: 'none' }
			})
		}
	}
}

function loadTooltip() {
	var tooltip = document.getElementsByClassName('tooltip')[0]
	setTimeout(() => {
		tooltip.style.setProperty('--scale', 1)
	}, 1000)
	setTimeout(() => {
		tooltip.style.setProperty('--scale', 0)
	}, 3000)
}

function usagebandListDisplay(usagebandList) {
	for (var j = 0; j < usagebandList.length; j++) {
		var currentState = usagebandList[j].style.display
		if (currentState === 'none') {
			usagebandList[j].style.display = 'block'
		} else {
			usagebandList[j].style.display = 'none'
		}
	}
}

export default {
	name: 'CoverageMap',
	async mounted() {
		//Fetch all the S-100 variants
		let s100variants = await axios.get(CONFIG.S100CoverageUrl).then(response => response.data)
		const map = new mapboxgl.Map({
			container: 'map',
			style: {
				version: 8,
				sources: {
					'simple-tiles': {
						type: 'raster',
						tiles: [
							'https://services.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}'						],
						tileSize: 256,
						'source-layer': 'simple-tiles'
					}
				},
				layers: [
					{
						id: 'simple-tiles',
						type: 'raster',
						source: 'simple-tiles',
						minzoom: 0,
						maxzoom: 22
					}
				]
			},
			center: [0, 13],
			zoom: 1,
			minZoom: 1
		})

		//Posibility of setting full-screen map
		//map.addControl(new mapboxgl.FullscreenControl())
		map.addControl(new mapboxgl.NavigationControl())
		map.addControl(
			new mapboxgl.AttributionControl({
				customAttribution:
					'Esri, HERE, Garmin,  OpenStreetMap contributors, and the GIS User Community | <a id ="disclaimer" class = "tooltip" data-tooltip="Alignment of boundaries is a presentation of the feature provided by our data vendors and does not imply endorsement by PRIMAR or any governing authority">DISCLAIMER</a>'
			})
		)

		// map['scrollZoom'].disable()

		map.on('load', function() {
			map.addSource('coverage', {
				type: 'vector',
				tiles: ['https://primar.ecc.no/primar/vectortile_ip/anonymous/coverage/{z}/{x}/{y}.mvt']
			})

			map.addLayer({
				id: 'ENC Coverage',
				type: 'fill',
				source: 'coverage',
				'source-layer': 'coverage',
				paint: {
					'fill-opacity': 0.3,
					'fill-color': '#FF9500', //  #ffffcc
					'fill-outline-color': '#00387A'
				},
				layout: {
					visibility: 'none'
				}
			})
			
			//All the usagebands for ENC Coverage
			loadUsageBands(map)
			// //All the s100 variants that are available in the API used + RTZCoverage
			loadS100Variants(map, s100variants)
			//Load tooltip to show on page load then hide
			loadTooltip()
		})

		map.on('idle', function() {
			var list = []
			var layers = map.getStyle().layers
			layers.forEach(layer => {
				if (layer.id !== 'simple-tiles') {
					list.push([layer.id, layer['source-layer'], layer.layout['visibility']])
				}
			})

			for (var i = 0; i < list.length; i++) {
				var id = list[i][0]
				var source_layer = list[i][1]
				var visibility = list[i][2]
				if (!document.getElementById(id)) {
					var link = document.createElement('a')
					link.id = id
					link.href = '#'
					link.textContent = id
					if (!document.getElementById('enc-coverage')) {
						i = i - 1 //Start again to get all layers
						link.id = 'enc-coverage'
						link.textContent = 'Show ENC Usagebands'
						link.className = 'active'
					} else if (source_layer === 'coverage' && !id.includes('Coverage')) {
						link.className = visibility === 'visible' ? 'usagebands-active' : 'usagebands'
						link.style.display = 'none'
					} else {
						link.className = visibility === 'visible' ? 'active' : ''
					}
					if(id === 'Nautical Publications Coverage' || id === 'WMS Coverage' ) {
							link.style.display = 'none'
					}
					link.onclick = function(e) {
						var clickedLayer = this.textContent
						
						if (clickedLayer.includes('Usagebands')) {
							this.textContent =
								this.textContent === 'Show ENC Usagebands' ? 'Hide ENC Usagebands' : 'Show ENC Usagebands'
							e.preventDefault()
							e.stopPropagation()
							var usagebandList = document.querySelectorAll('.usagebands-active,.usagebands')
							usagebandListDisplay(usagebandList)
						} else {
							var visibility = map.getLayoutProperty(clickedLayer, 'visibility')
							e.preventDefault()
							e.stopPropagation()
							if (visibility === 'visible') {
								map.setLayoutProperty(clickedLayer, 'visibility', 'none')
								this.className = this.className === 'usagebands-active' ? 'usagebands' : ''
							} else {
								this.className = this.className === 'usagebands' ? 'usagebands-active' : 'active'
								map.setLayoutProperty(clickedLayer, 'visibility', 'visible')
							}
						}
					}
					layers =
						link.className === 'usagebands' || link.className === 'usagebands-active'
							? document.getElementById('usagebands-list')
							: document.getElementById('menu')
					layers.appendChild(link)
				}
			}
		})
	}
}
</script>

<style lang="scss" scoped>
#map {
	width: 100%;
	height: 600px;
}
</style>
<style lang="scss">
#menu {
	background-color: #3074a4;
	z-index: 1;
	position: relative;
	bottom: 0;
	border-radius: 3px;
	border: 1px solid rgba(0, 0, 0, 0.4);
	font-family: 'Work Sans', sans-serif;
}

#menu a {
	font-size: px;
	color: #fff;
	display: block;
	margin: 0;
	padding: 10px;
	text-decoration: none;
	border-bottom: 1px solid rgba(0, 0, 0, 0.25);
	text-align: center;
	height: max-content;
	//Non-selectable text
	-webkit-touch-callout: none; /* iOS Safari */
	-webkit-user-select: none; /* Safari */
	-khtml-user-select: none; /* Konqueror HTML */
	-moz-user-select: none; /* Old versions of Firefox */
	-ms-user-select: none; /* Internet Explorer/Edge */
	user-select: none; /* Non-prefixed version, currently
                                  supported by Chrome, Edge, Opera and Firefox */
}

#menu a:last-child {
	border: none;
}

#menu a.active,
a.usagebands-active {
	background: #00387a;
	color: #ffffff;
}

#usagebands-list {
	background-color: #3074a4;
	position: absolute;
	z-index: 1;
	bottom: 0px;
	left: 100%;
	margin-left: 0.5%;
	border-radius: 3px;
	border: 1px solid rgba(0, 0, 0, 0.4);
	font-family: 'Work Sans', sans-serif;
}

#usagebands-list a {
	color: #fff;
	height: 30px;
	padding: 10px;
	text-decoration: none;
	border-bottom: 1px solid rgba(0, 0, 0, 0.25);
	text-align: center;
}

#usagebands-list a:last-child {
	border: none;
}

.tooltip {
	--scale: 0;
	position: relative;
	z-index: 1;
	font-family: 'Work Sans', sans-serif;
	width: max-content;
	cursor: pointer;
}

#menu-tooltip::before,
#menu-tooltip::after {
	z-index: 1;
	position: absolute;
	top: 1.5rem;
	left: 0;
	transform: translateX(20%) translateY(-125%) scale(var(--scale));
	transition: 250ms transform;
	transform-origin: bottom left;
}

#disclaimer::before,
#disclaimer::after {
	text-decoration: none;
	z-index: 1;
	position: absolute;
	top: 1.5rem;
	left: 0;
	transform: translateX(-70%) translateY(-130%) scale(var(--scale));
	transition: 250ms transform;
	transform-origin: bottom right;
}

#menu-tooltip::before {
	background: white;
	content: attr(data-tooltip);
	color: black;
	width: 120px;
	max-width: 150px;
	z-index: 1;
	padding: 0.5rem;
	border-radius: 0.5rem;
}

#disclaimer::before {
	background: white;
	content: attr(data-tooltip);
	color: black;
	z-index: 1;
	width: 250px;
	padding: 0.5rem;
	border-radius: 0.5rem;
}

.tooltip:hover::before,
.tooltip:hover::after {
	--scale: 1;
}

.menu-container {
	z-index: 1;
	position: absolute;
	display: grid;
	bottom: 10px;
	left: 10px;
	max-height: 100%;
	width: auto;
	cursor: pointer;
}

.menu {
	width: 150px;
}
</style>
